/**
 * siege-command-menu.ts — MFix5.4 (2026-07-22).
 *
 * Small DOM overlay opened when the player clicks the crossed-swords
 * siege icon over a besieged city. Presents two buttons:
 *
 *   - **Assault** — fires an `assault_settlement` command (existing v0M4.2.5
 *     path). Spawns a proper siege battle with wall breach + garrison
 *     combat.
 *   - **Continue Siege** — dismisses the menu. The auto-siege is
 *     already active per MFix3.2 / MFix5.1; this button is really just
 *     "keep waiting for the surrender timer."
 *
 * Only opens for the player's own sieges (hit-test filters foreign
 * sieges out — see `findSiegeAtScreen` in hit-test.ts).
 *
 * The `enqueue` callback dispatches the command up to the sim driver
 * (matches the city-panel + battle-command-modal pattern).
 */

import type { Command } from '../../sim/commands';
import type { SettlementId, FactionId, GameState } from '../../sim/state';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface SiegeCommandMenuHandle {
  /** Called each frame; refreshes label values without rebuilding DOM. */
  update(state: GameState): void;
  /** Removes the overlay from the DOM. */
  close(): void;
}

export function openSiegeCommandMenu(args: {
  container: HTMLElement;
  state: GameState;
  settlementId: SettlementId;
  playerFactionId: FactionId;
  screenX: number;
  screenY: number;
  enqueue: (cmd: Command) => void;
  onClose: () => void;
}): SiegeCommandMenuHandle | null {
  const city = args.state.settlements.get(args.settlementId);
  if (!city || city.siegeState === null) return null;
  const sieger = args.state.armies.get(city.siegeState.siegingArmyId);
  if (!sieger) return null;
  if (sieger.ownerFactionId !== args.playerFactionId) return null;

  const overlay = document.createElement('div');
  overlay.className = 'siege-command-menu';
  overlay.style.position = 'absolute';
  // Position slightly to the lower-right of the click so the menu
  // doesn't obscure the icon Michael just clicked.
  overlay.style.left = `${args.screenX + 20}px`;
  overlay.style.top = `${args.screenY + 10}px`;
  overlay.style.background = 'rgba(26, 20, 10, 0.95)';
  overlay.style.border = '2px solid #f4d47a';
  overlay.style.borderRadius = '4px';
  overlay.style.padding = '8px 10px';
  overlay.style.fontFamily = '"Trajan Pro", "Cinzel", "Times New Roman", serif';
  overlay.style.color = '#f5eacf';
  overlay.style.zIndex = '2000';
  overlay.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.6)';
  overlay.style.minWidth = '180px';

  const header = document.createElement('div');
  header.style.fontWeight = 'bold';
  header.style.fontSize = '13px';
  header.style.marginBottom = '6px';
  header.style.paddingBottom = '4px';
  header.style.borderBottom = '1px solid #4a3820';
  header.textContent = 'Command Siege';
  overlay.appendChild(header);

  const daysLabel = document.createElement('div');
  daysLabel.style.fontSize = '11px';
  daysLabel.style.opacity = '0.85';
  daysLabel.style.marginBottom = '8px';
  const days = Math.max(0, Math.ceil(city.siegeState.daysRemaining));
  daysLabel.textContent = `${days} days until surrender`;
  overlay.appendChild(daysLabel);

  const assaultBtn = document.createElement('button');
  assaultBtn.textContent = 'Assault';
  styleMenuButton(assaultBtn, /* primary */ true);
  assaultBtn.onclick = () => {
    // AssaultSettlementCommand takes only the sieging army id (the city is
    // implied by the army's active siege lock). Playfaction is stamped
    // for authoritative validation; tick is stamped by sim-driver on
    // dispatch.
    args.enqueue({
      type: 'assault_settlement',
      tick: 0,
      playerFactionId: args.playerFactionId,
      armyId: city.siegeState!.siegingArmyId,
    });
    args.onClose();
  };
  overlay.appendChild(assaultBtn);

  const continueBtn = document.createElement('button');
  continueBtn.textContent = 'Continue Siege';
  styleMenuButton(continueBtn, /* primary */ false);
  continueBtn.onclick = () => {
    args.onClose();
  };
  overlay.appendChild(continueBtn);

  // Close on click outside — capture-phase document listener that
  // ignores clicks inside the overlay.
  const outsideClickHandler = (e: MouseEvent) => {
    if (!overlay.contains(e.target as Node)) {
      args.onClose();
    }
  };
  // Defer to next tick so the click that opened this menu doesn't
  // immediately close it via the document listener.
  setTimeout(() => document.addEventListener('mousedown', outsideClickHandler), 0);

  args.container.appendChild(overlay);

  return {
    update(state: GameState) {
      const c = state.settlements.get(args.settlementId);
      if (!c || c.siegeState === null) {
        // Siege ended (capture, break condition, etc.) — auto-close.
        args.onClose();
        return;
      }
      const d = Math.max(0, Math.ceil(c.siegeState.daysRemaining));
      daysLabel.textContent = `${d} days until surrender`;
    },
    close() {
      document.removeEventListener('mousedown', outsideClickHandler);
      overlay.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Styling helpers
// ---------------------------------------------------------------------------

function styleMenuButton(btn: HTMLButtonElement, primary: boolean): void {
  btn.style.display = 'block';
  btn.style.width = '100%';
  btn.style.padding = '6px 10px';
  btn.style.marginTop = '4px';
  btn.style.background = primary ? '#8a2828' : '#3a2c18';
  btn.style.color = '#f5eacf';
  btn.style.border = primary ? '1px solid #d47070' : '1px solid #6a5238';
  btn.style.borderRadius = '3px';
  btn.style.fontFamily = 'inherit';
  btn.style.fontSize = '12px';
  btn.style.cursor = 'pointer';
  btn.style.transition = 'background 0.15s';
  btn.onmouseenter = () => {
    btn.style.background = primary ? '#a83535' : '#4d3a20';
  };
  btn.onmouseleave = () => {
    btn.style.background = primary ? '#8a2828' : '#3a2c18';
  };
}
